import React from 'react';

import { css } from '@compiled/react';

import Avatar from '@atlaskit/avatar';
import TaskIcon from '@atlaskit/icon/core/task';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

import type { IssueTypeIconName } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/onboarding-wizard/wizard/assets/issue-type-svgs';
import { IssueTypeIcon } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/onboarding-wizard/wizard/assets/issue-type-svgs';

type BoardViewProps = {
	columnNames?: string[];
	issueTypesIcons?: IssueTypeIconName[];
};

/**
 * Kanban board illustration for the JFind onboard illustration.
 * Renders 3 scrollable columns with placeholder issue cards.
 * Column names default to To do / In progress / Complete but can be
 * overridden to reflect the user's work status elections.
 */
export const JfindIllustrationBoardView = ({
	columnNames = ['To do', 'In progress', 'Complete'],
	issueTypesIcons,
}: BoardViewProps) => {
	const boardRef = React.useRef<HTMLDivElement>(null);
	const prevLengthRef = React.useRef(columnNames.length);

	React.useEffect(() => {
		if (boardRef.current && columnNames.length > prevLengthRef.current) {
			boardRef.current.scrollTo({ left: boardRef.current.scrollWidth, behavior: 'smooth' });
		}
		prevLengthRef.current = columnNames.length;
	}, [columnNames.length]);

	return (
		<div css={boardScrollWrapperStyles} ref={boardRef}>
			<Flex xcss={boardStyles} gap="space.150">
				{columnNames.map((name, colIndex) => (
					<Box key={colIndex} xcss={columnStyles}>
						<div css={columnHeaderStyles}>{name.trim() || 'STATUS'}</div>
						<Flex xcss={columnScrollableStyles} direction="column" gap="space.100">
							{colIndex === 0
								? issueTypesIcons && issueTypesIcons.length > 0
									? issueTypesIcons.map((icon) => (
											<JfindIllustrationIssueCard key={icon} issueTypeIcon={icon} />
										))
									: [<JfindIllustrationIssueCard key="default" />]
								: [
										<JfindIllustrationIssueCard key="s1" />,
										<JfindIllustrationIssueCard key="s2" />,
										<JfindIllustrationIssueCard key="s3" />,
									]}
						</Flex>
					</Box>
				))}
			</Flex>
		</div>
	);
};

type IssueCardProps = {
	issueTypeIcon?: IssueTypeIconName;
};

/**
 * A single placeholder issue card for the kanban board illustration.
 */
export const JfindIllustrationIssueCard = ({ issueTypeIcon }: IssueCardProps) => (
	<Box xcss={issueCardStyles}>
		{issueTypeIcon ? (
			<div css={cardTitleTextStyles}>
				{issueTypeIcon.charAt(0).toUpperCase() + issueTypeIcon.slice(1)}
			</div>
		) : (
			<Skeleton width={122} height={7} borderRadius={3} />
		)}
		<Flex xcss={cardFooterStyles} alignItems="center" gap="space.050">
			{issueTypeIcon ? (
				<IssueTypeIcon type={issueTypeIcon} />
			) : (
				<TaskIcon label="" color={token('color.icon.disabled')} />
			)}
			<Skeleton width={48} height={7} borderRadius={3} />
			<Box xcss={cardAvatarSpacerStyles} />
			<Avatar size="xsmall" />
		</Flex>
	</Box>
);

const boardScrollWrapperStyles = css({
	overflowX: 'auto',
	overflowY: 'visible',
	flex: 1,
	display: 'flex',
});

const boardStyles = xcss({
	paddingInlineStart: 'space.400',
	paddingBlockStart: 'space.0',
	flex: 1,
});

const columnStyles = xcss({
	backgroundColor: 'elevation.surface.sunken',
	borderRadius: 'border.radius.100',
	width: '195px',
	height: '436px',
	flexShrink: 0,
	display: 'flex',
	flexDirection: 'column',
	overflow: 'hidden',
});

const columnHeaderStyles = css({
	paddingBlockStart: token('space.200'),
	paddingInline: token('space.100'),
	fontSize: '14px',
	fontWeight: 700,
	color: token('color.text.subtle'),
	textTransform: 'uppercase',
	lineHeight: '16px',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
});

const columnScrollableStyles = xcss({
	padding: 'space.100',
	overflowY: 'auto',
	overflowX: 'hidden',
	flex: 1,
});

const issueCardStyles = xcss({
	backgroundColor: 'elevation.surface',
	borderRadius: 'border.radius.100',
	padding: 'space.150',
	boxShadow: 'elevation.shadow.raised',
	display: 'flex',
	flexDirection: 'column',
	gap: 'space.250',
	flexShrink: 0,
});

const cardFooterStyles = xcss({
	height: '16px',
	flexShrink: 0,
});

const cardAvatarSpacerStyles = xcss({
	flex: 1,
});

const cardTitleTextStyles = css({
	font: token('font.body.small'),
	fontWeight: token('font.weight.medium'),
	color: token('color.text'),
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
});
